// Category review workflow (review V2 points 1–4, BLD-28), shared by server and client. An assigned category moves
// open → submitted (answerer) → validated or returned (group manager) → approved (consultant). The consultant may
// edit answers and approve at any point while the scenario is a draft, and may return a category to its answerer.
// Unassigned categories have no workflow: the consultant answers them directly, as before.
import { z } from "zod";
import { MATURITY_LIMITS } from "./definition.ts";
import { ANSWERS } from "./responses.ts";

export const CATEGORY_STATES = ["open", "submitted", "returned", "validated", "approved"] as const;
export type CategoryState = (typeof CATEGORY_STATES)[number];

export const CATEGORY_STATE_LABEL: Record<CategoryState, string> = {
  open: "Atribuída",
  submitted: "Enviada para validação",
  returned: "Devolvida",
  validated: "Validada pelo gestor",
  approved: "Aprovada",
};
export const CATEGORY_STATE_TONE: Record<CategoryState, "gray" | "blue" | "gold" | "green"> = {
  open: "gray",
  submitted: "blue",
  returned: "gold",
  validated: "blue",
  approved: "green",
};

export const NOTE_MAX = 2000;

/** The answerer may edit while the category is open or returned. */
export const answererCanEdit = (s: CategoryState) => s === "open" || s === "returned";

export interface CategoryStateInfo {
  state: CategoryState;
  version: number;
  note: string | null;
  updated_at: string | null;
  updated_by_name: string | null;
}
export const OPEN_STATE: CategoryStateInfo = { state: "open", version: 0, note: null, updated_at: null, updated_by_name: null };

export const versionSchema = z.number().int().min(0);
export const transitionSchema = z.strictObject({ expected_version: versionSchema });
export const returnSchema = z.strictObject({
  expected_version: versionSchema,
  note: z.string().trim().min(1, "Explique o que precisa ser ajustado.").max(NOTE_MAX),
});

/** What a participant may write: the answer and the group comment (units and consultant comments stay the consultant's). */
export const participantEntrySchema = z.strictObject({
  answer: z.enum(ANSWERS).nullable().default(null),
  group_comment: z.string().max(MATURITY_LIMITS.comment).nullable().default(null),
});
export const participantSaveSchema = z.strictObject({
  expected_version: versionSchema,
  responses: z.record(z.string(), participantEntrySchema),
});
